import { useRef, type FC, type ReactNode, type Ref } from "react";

import { OnboardingDotGridCanvas } from "@/components/backgrounds/OnboardingDotGridCanvas";
import { withDesktopDrag } from "@/components/desktop/withDesktopDrag";
import type { OnboardingStepColorTheme } from "@/routes/ona-onboarding/onboarding-color-themes";
import { OnboardingHeader } from "@/routes/ona-onboarding/OnboardingHeader";

type OnboardingLayoutProps = {
    children: ReactNode;
    /** Render function that receives a ref to attach to the showcase content wrapper. */
    renderShowcase: (showcaseRef: Ref<HTMLDivElement>) => ReactNode;
    /** Current step identifier — used for keying transitions. */
    stepKey: string;
    /** Per-step color theme for the right panel background and dot grid. */
    stepColorTheme?: OnboardingStepColorTheme;
};

export const OnboardingLayout: FC<OnboardingLayoutProps> = withDesktopDrag(
    ({ children, renderShowcase, stepColorTheme }) => {
        const showcaseRef = useRef<HTMLDivElement>(null);

        return (
            <div className="flex h-full min-h-screen w-full flex-col md:flex-row">
                {/* Left panel — header + form + banner */}
                <div className="flex w-full flex-1 flex-col bg-surface-primary md:w-1/2 md:border-r md:border-border-base">
                    <OnboardingHeader />

                    {/* Centered form content */}
                    <div className="flex flex-1 animate-fade-in flex-col items-center justify-center px-6 py-8 md:px-16">
                        {children}
                    </div>
                </div>

                {/* Right panel — dot grid background (hidden on mobile) */}
                <div
                    className="relative hidden md:block md:w-1/2"
                    style={{
                        backgroundColor: stepColorTheme?.bg ?? "rgb(var(--surface-base))",
                        transition: "background-color 500ms ease",
                    }}
                >
                    {/* Tinted overlay for dark mode — hue color at low opacity over bg-surface-base */}
                    {stepColorTheme?.overlay && (
                        <div
                            className="pointer-events-none absolute inset-0"
                            style={{
                                backgroundColor: stepColorTheme.overlay.color,
                                opacity: stepColorTheme.overlay.opacity,
                                transition: "background-color 500ms ease, opacity 500ms ease",
                            }}
                        />
                    )}
                    {stepColorTheme && (
                        <OnboardingDotGridCanvas
                            animationMode={stepColorTheme.animationMode}
                            colorTheme={stepColorTheme.dots}
                        />
                    )}
                    <div className="relative flex h-full w-full animate-fade-in items-center justify-center p-16">
                        {renderShowcase(showcaseRef)}
                    </div>
                </div>
            </div>
        );
    },
);
